import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Modal from '../../Modal';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';
import { useSpacesUpload, validateFiles, ACCEPT_STRING } from '../../../lib/useSpacesUpload';
import { PendingFilePreview } from '../../shared/AttachmentComponents';
import { memberDisplayName } from '../../../lib/format';
import { useMentionInContentEditable } from '../../../hooks/useMentionInContentEditable';

function defaultDueOn() {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
}

const SERVICE_MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const PRIORITY_OPTIONS = [
  { value: 'low',      label: 'Low',      color: '#22c55e' },
  { value: 'medium',   label: 'Medium',   color: '#3b82f6' },
  { value: 'high',     label: 'High',     color: '#f59e0b' },
  { value: 'urgent',   label: 'Urgent',   color: '#ef4444' },
];

const EST_PRESETS = [
  { value: 15,  label: '15m' },
  { value: 30,  label: '30m' },
  { value: 45,  label: '45m' },
  { value: 60,  label: '1h' },
  { value: 120, label: '2h' },
  { value: 240, label: '4h' },
  { value: 480, label: '8h' },
];

// Hourly rate divisor for per-hour project billing — mirrors backend/src/routes/hr/projects.js
const CTC_HOURLY_DIVISOR = 1920; // 12 months x 20 working days x 8 hours

const TASK_TYPE_OPTIONS = [
  { value: 'internal', label: 'Internal' },
  { value: 'project',  label: 'Project'  },
  { value: 'contract', label: 'Agreement' },
];

const CHIP_MAX = 4;

/* ── tiny SVG helpers ─────────────────────────────────────────────────────── */

function FlagIcon({ color }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2 1v10" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M2 2h8L8 5.5 10 9H2" stroke={color} strokeWidth="1.3"
        strokeLinecap="round" strokeLinejoin="round" fill={color + '25'} />
    </svg>
  );
}

function TaskTypeIcon({ type, size = 14 }) {
  const s = { width: size, height: size };
  if (type === 'project') return (
    <svg {...s} viewBox="0 0 14 14" fill="none">
      <path d="M1.5 5h11v6.5a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V5z" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 5V3.5a1 1 0 0 1 1-1h3l1.5 1.5H12a1 1 0 0 1 1 1V5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
  if (type === 'contract') return (
    <svg {...s} viewBox="0 0 14 14" fill="none">
      <rect x="2" y="1.5" width="10" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4.5 5h5M4.5 7.5h5M4.5 10h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
  return (
    <svg {...s} viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="3" width="11" height="9.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4.5 3V2a2.5 2.5 0 0 1 5 0v1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

/* ── Chip button ─────────────────────────────────────────────────────────── */

function Chip({ selected, onClick, children, accentColor, disabled }) {
  const accent = accentColor || 'var(--accent)';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '5px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 500,
        border: selected ? `2px solid ${accent}` : '1.5px solid var(--border)',
        background: selected ? accent + '14' : 'var(--bg-card)',
        color: selected ? accent : 'var(--text-secondary)',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.12s',
        whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'left',
        maxWidth: '100%',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

/* ── MemberAvatar ────────────────────────────────────────────────────────── */

function MemberAvatar({ member, size = 26 }) {
  const initials = member
    ? ((member.first_name?.[0] || '') + (member.last_name?.[0] || '')).toUpperCase() || '?'
    : '?';
  const COLORS = ['#6366f1','#3b82f6','#06b6d4','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];
  const ci = member ? (member.user_id % COLORS.length) : 0;
  if (member?.photo_url || member?.avatar_url) return (
    <img src={member.photo_url || member.avatar_url} alt=""
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
  );
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: COLORS[ci], color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.37, fontWeight: 700,
    }}>
      {initials}
    </div>
  );
}

/* ── Section card wrapper ────────────────────────────────────────────────── */

function Card({ children, style }) {
  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: '13px 15px',
      background: 'var(--bg-card)',
      minWidth: 0,
      overflow: 'hidden',
      ...style,
    }}>
      {children}
    </div>
  );
}

function CardLabel({ icon, text, required }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
      fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', lineHeight: 1,
    }}>
      <span style={{ display: 'flex', flexShrink: 0, marginBottom: 2 }}>{icon}</span>
      {text}
      {required && <span style={{ color: '#e05555' }}>*</span>}
    </div>
  );
}


/* ── Icons for card labels ───────────────────────────────────────────────── */

const IC = {
  taskName: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <rect x="1.5" y="1.5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 5h5M4 7h5M4 9h3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
  desc: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M1.5 3.5h10M1.5 6.5h7M1.5 9.5h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  calendar: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <rect x="1.5" y="2.5" width="10" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 1.5v2M9 1.5v2M1.5 5h10" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
  user: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <circle cx="6.5" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 11.5c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  flag: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M2.5 1.5v10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M2.5 2.5h9L9 5.5 11.5 9H2.5" stroke="currentColor" strokeWidth="1.2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  clock: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <circle cx="6.5" cy="7" r="5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6.5 4.5v2.5l1.5 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  type: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <rect x="1.5" y="1.5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 5h5M4 7h5M4 9h3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
  role: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M6.5 1.5L1.5 4.5v7h11v-7L6.5 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M4.5 11.5V8h4v3.5" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  ),
  area: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="6.5" cy="6.5" r="2" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  ),
  routine: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M2 6.5C2 4 4 2 6.5 2c1.5 0 2.8.7 3.7 1.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M11 6.5C11 9 9 11 6.5 11c-1.5 0-2.8-.7-3.7-1.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M9.5 2.5l1.5 1-1 1.5M3.5 10.5l-1.5-1 1-1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  search: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <circle cx="5.5" cy="5.5" r="3.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M8.5 8.5l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  xCircle: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 5l4 4M9 5l-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  pencil: (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
      <path d="M1.5 8.5l5.5-5.5 2 2L3.5 10.5H1.5v-2z" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.5 3.5l1-1 2 2-1 1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chevronDown: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3.5 5.5l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function CreateTaskModal({
  open, onClose, members, roles, areasByRole, onCreated, defaultProjectId,
  editTask, onUpdated, defaultAssigneeUserId, businessReviewId, businessReviewFor,
  defaultContractId, defaultAgreementServiceId, defaultServiceMonth, defaultServiceYear, lockTaskType,
}) {
  const isEditMode = !!editTask;
  const { appId } = useParams();
  const api       = useApi();
  const { me, myRole } = useWorkspace();
  const isAdmin = myRole === 'super_admin' || myRole === 'admin';

  const defaultProjectIdRef = useRef(defaultProjectId);
  defaultProjectIdRef.current = defaultProjectId;
  const defaultAssigneeUserIdRef = useRef(defaultAssigneeUserId);
  defaultAssigneeUserIdRef.current = defaultAssigneeUserId;
  const defaultContractIdRef = useRef(defaultContractId);
  defaultContractIdRef.current = defaultContractId;
  const defaultAgreementServiceIdRef = useRef(defaultAgreementServiceId);
  defaultAgreementServiceIdRef.current = defaultAgreementServiceId;
  const defaultServiceMonthRef = useRef(defaultServiceMonth);
  defaultServiceMonthRef.current = defaultServiceMonth;
  const defaultServiceYearRef = useRef(defaultServiceYear);
  defaultServiceYearRef.current = defaultServiceYear;

  /* ── form state ── */
  const [title, setTitle]               = useState('');
  const [description, setDescription]   = useState('');
  const [priority, setPriority]         = useState('medium');
  const [dueOn, setDueOn]               = useState('');
  const [estMinutes, setEstMinutes]     = useState(45);
  const [customEst, setCustomEst]       = useState(false);
  const [customEstInput, setCustomEstInput] = useState('');
  const [assigneeId, setAssigneeId]     = useState('');
  const [roleId, setRoleId]             = useState('');
  const [areaId, setAreaId]             = useState('');
  const [routineId, setRoutineId]       = useState('');

  /* ── expand for chip overflow ── */
  const [roleExpanded, setRoleExpanded]       = useState(false);
  const [areaExpanded, setAreaExpanded]       = useState(false);
  const [routineExpanded, setRoutineExpanded] = useState(false);

  /* ── assignee role assignments ── */
  const [assigneeRoles, setAssigneeRoles] = useState([]);

  /* ── assignee inline search ── */
  const [assigneeSearch, setAssigneeSearch] = useState('');

  /* ── task type ── */
  const [taskType, setTaskType]             = useState('internal');
  const [projectId, setProjectId]           = useState('');
  const [projectSearch, setProjectSearch]   = useState('');
  const [projects, setProjects]             = useState([]);
  const [contractId, setContractId]         = useState('');
  const [contractSearch, setContractSearch] = useState('');
  const [contracts, setContracts]           = useState([]);
  const [contractDropOpen, setContractDropOpen] = useState(false);
  const [milestoneId, setMilestoneId]       = useState('');
  const [milestones, setMilestones]         = useState([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [milestoneDropOpen, setMilestoneDropOpen] = useState(false);
  const [agreementServiceId, setAgreementServiceId] = useState('');
  const [agreementServices, setAgreementServices]   = useState([]);
  const [agreementServicesLoading, setAgreementServicesLoading] = useState(false);
  const [serviceDropOpen, setServiceDropOpen] = useState(false);
  const [serviceSearch, setServiceSearch]     = useState('');
  const [serviceMonth, setServiceMonth]       = useState('');
  const [serviceYear, setServiceYear]         = useState('');

  /* ── routines ── */
  const [routineGroups, setRoutineGroups]     = useState({});
  const [routinesLoading, setRoutinesLoading] = useState(false);

  /* ── workload / deadline suggestion ── */
  const [assigneeWorkload, setAssigneeWorkload] = useState(null); // { pending_minutes, min_hours_per_day }

  /* ── checklist (create mode: local list; edit mode: live API) ── */
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistInput, setChecklistInput] = useState('');
  const [editChecklist, setEditChecklist]   = useState([]);
  const [editChkInput, setEditChkInput]     = useState('');
  const [editChkAdding, setEditChkAdding]   = useState(false);

  /* ── submit ── */
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);
  const [joinSent, setJoinSent]       = useState(false);
  const [confirmJoin, setConfirmJoin] = useState(false);

  /* ── description attachments ── */
  const [descPendingFiles, setDescPendingFiles] = useState([]);
  const [descFileErrors,   setDescFileErrors]   = useState([]);
  const [descUploading, setDescUploading]       = useState(false);
  const [descCharCount, setDescCharCount]       = useState(0);
  const descFileInputRef = useRef(null);
  const descRef          = useRef(null);
  const descSavedRange   = useRef(null);
  const { uploadFiles: uploadDescFiles } = useSpacesUpload(appId, 'task_description');

  const {
    handleMentionKeyDown: descMentionKeyDown,
    handleMentionInput:   descMentionInput,
    MentionDropdown:      DescMentionDropdown,
    closeMention:         descCloseMention,
  } = useMentionInContentEditable(descRef, members);

  const descSaveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (descRef.current?.contains(r.commonAncestorContainer)) descSavedRange.current = r.cloneRange();
    }
  };

  const descRestoreRange = () => {
    const r = descSavedRange.current;
    if (!r) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
  };

  const openVideoModal = (src, name) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.88);display:flex;flex-direction:column;align-items:center;justify-content:center;';
    const inner = document.createElement('div');
    inner.style.cssText = 'position:relative;max-width:90vw;max-height:90vh;display:flex;flex-direction:column;align-items:center;';
    inner.addEventListener('click', e => e.stopPropagation());
    const header = document.createElement('div');
    header.style.cssText = 'width:100%;display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:rgba(0,0,0,0.5);border-radius:8px 8px 0 0;gap:12px;box-sizing:border-box;';
    const title = document.createElement('span');
    title.textContent = name;
    title.style.cssText = 'font-size:13px;color:#fff;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;';
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = 'background:none;border:none;color:#fff;cursor:pointer;font-size:16px;padding:4px;flex-shrink:0;';
    closeBtn.addEventListener('click', () => document.body.removeChild(overlay));
    header.appendChild(title);
    header.appendChild(closeBtn);
    const body = document.createElement('div');
    body.style.cssText = 'background:#111;border-radius:0 0 8px 8px;overflow:hidden;display:flex;align-items:center;justify-content:center;';
    const video = document.createElement('video');
    video.src = src;
    video.controls = true;
    video.autoplay = true;
    video.style.cssText = 'max-width:88vw;max-height:calc(90vh - 48px);display:block;';
    body.appendChild(video);
    inner.appendChild(header);
    inner.appendChild(body);
    overlay.appendChild(inner);
    overlay.addEventListener('click', () => { video.pause(); document.body.removeChild(overlay); });
    document.body.appendChild(overlay);
  };

  const makeAttachmentNode = ({ file, savedAtt }) => {
    const ct = file ? file.type : savedAtt?.content_type;
    const isImg = ct?.startsWith('image/');
    const isVid = ct?.startsWith('video/');
    const src = file ? URL.createObjectURL(file) : savedAtt?.file_url;
    const name = file ? file.name : (savedAtt?.file_name || 'file');
    const wrapper = document.createElement('span');
    wrapper.className = 'cmt-inline-img-wrap';
    wrapper.contentEditable = 'false';
    if (file) wrapper._file = file;
    if (savedAtt) wrapper._savedAtt = savedAtt;
    if (isImg) {
      const img = document.createElement('img');
      img.src = src;
      img.className = 'cmt-inline-img-thumb';
      img.alt = name;
      wrapper.appendChild(img);
    } else if (isVid) {
      const vid = document.createElement('video');
      vid.src = src + '#t=0.1';
      vid.className = 'cmt-inline-img-thumb cmt-inline-vid-thumb';
      vid.preload = 'metadata';
      vid.muted = true;
      vid.playsInline = true;
      vid.style.pointerEvents = 'none';
      wrapper.appendChild(vid);
      const play = document.createElement('span');
      play.className = 'cmt-inline-vid-play';
      play.textContent = '▶';
      play.style.pointerEvents = 'none';
      wrapper.appendChild(play);
      wrapper.style.cursor = 'pointer';
      wrapper.addEventListener('click', (e) => {
        if (e.target === wrapper || e.target === vid || e.target === play) {
          openVideoModal(src, name);
        }
      });
    } else {
      const chip = document.createElement('span');
      chip.className = 'cmt-inline-file-chip';
      chip.textContent = '📎 ' + name;
      wrapper.appendChild(chip);
    }
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'cmt-inline-img-remove';
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', () => { wrapper.remove(); setDescFileErrors([]); });
    wrapper.appendChild(removeBtn);
    return wrapper;
  };

  const insertDescFiles = (files) => {
    const el = descRef.current;
    if (!el) return;
    el.focus();
    descRestoreRange();
    const sel = window.getSelection();
    files.forEach((file) => {
      const wrapper = makeAttachmentNode({ file });
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.insertNode(wrapper);
        range.setStartAfter(wrapper);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        descSavedRange.current = range.cloneRange();
      } else {
        el.appendChild(wrapper);
      }
    });
  };

  const extractDescData = () => {
    const el = descRef.current;
    if (!el) return { text: '', files: [], keptAtts: [] };
    const files = [];
    const keptAtts = [];
    let text = '';
    let newFileIdx = 0;
    const walk = (node, isFirst) => {
      if (node.nodeType === Node.TEXT_NODE) { text += node.textContent; }
      else if (node.nodeName === 'BR') {
        // Ignore a lone <br> that is the only child of a <div> — the div itself
        // already emits the newline, so this <br> is just a browser placeholder.
        if (node.parentNode?.nodeName !== 'DIV' || node.parentNode.childNodes.length > 1) {
          text += '\n';
        }
      }
      else if (node.nodeName === 'DIV') {
        // Each div is a new line, but skip the leading newline for the very first div
        // so we don't prepend a blank line before the content.
        if (!isFirst) text += '\n';
        node.childNodes.forEach((child) => walk(child, false));
      }
      else if (node.dataset?.mentionId) {
        text += `@[${node.dataset.mentionId}:${node.dataset.mentionName || node.textContent.slice(1)}]`;
      }
      else if (node._file instanceof File) {
        // Use a unique index-based key so duplicate filenames don't collide
        text += `\n[attachment: __new_${newFileIdx}__]\n`;
        files.push({ file: node._file, key: `__new_${newFileIdx}__` });
        newFileIdx++;
      }
      else if (node._savedAtt) {
        keptAtts.push(node._savedAtt);
        // Use id as key — always unique, never collides even if filenames match
        text += `\n[attachment: __id_${node._savedAtt.id}__]\n`;
      }
      else { node.childNodes.forEach((child) => walk(child, false)); }
    };
    el.childNodes.forEach((child, i) => walk(child, i === 0));
    return { text: text.replace(/^\n|\n$/g, ''), files, keptAtts };
  };

  const DESC_TEXT_LIMIT = 10000;

  const countDescTextChars = () => {
    const el = descRef.current;
    if (!el) return 0;
    let count = 0;
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) { count += node.textContent.length; return; }
      if (node._file instanceof File || node._savedAtt) return; // skip attachment nodes
      node.childNodes.forEach(walk);
    };
    el.childNodes.forEach(walk);
    return count;
  };

  const MAX_DESC_ATTACHMENTS = 50;
  const addDescFiles = (incoming) => {
    const { valid, errors } = validateFiles(Array.from(incoming));
    const current = descRef.current ? descRef.current.querySelectorAll('.cmt-inline-img-wrap').length : 0;
    const slots = MAX_DESC_ATTACHMENTS - current;
    if (slots <= 0) {
      setDescFileErrors([`Maximum ${MAX_DESC_ATTACHMENTS} attachments allowed`]);
      return;
    }
    const allowed = valid.slice(0, slots);
    const overflow = valid.length - allowed.length;
    const allErrors = [...errors, ...(overflow > 0 ? [`${overflow} file(s) skipped — maximum ${MAX_DESC_ATTACHMENTS} attachments reached`] : [])];
    setDescFileErrors(allErrors);
    if (allowed.length) insertDescFiles(allowed);
  };

  const dateInputRef = useRef(null);
  const meRef      = useRef(me);
  const membersRef = useRef(members);
  meRef.current      = me;
  membersRef.current = members;

  /* ── load projects ── */
  useEffect(() => {
    if (!open || !appId) return;
    api.listProjectsSimple(appId)
      .then(rows => {
        setProjects(rows || []);
        if (defaultProjectIdRef.current) {
          setProjectId(String(defaultProjectIdRef.current));
          setTaskType('project');
          const p = (rows || []).find(r => String(r.id) === String(defaultProjectIdRef.current));
          if (p) setProjectSearch(p.name);
        } else if (editTask?.project_id) {
          const p = (rows || []).find(r => String(r.id) === String(editTask.project_id));
          if (p) setProjectSearch(p.name);
        }
      })
      .catch(() => setProjects([]));
  }, [open, appId, api]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── load milestones for the selected project — only not-yet-completed ones ── */
  useEffect(() => {
    if (!open || !appId || !projectId) { setMilestones([]); setMilestoneId(''); return; }
    setMilestonesLoading(true);
    api.listMilestones(appId, projectId)
      .then(rows => {
        const incomplete = (rows || []).filter(m => !m.is_completed);
        setMilestones(incomplete);
        setMilestoneId(prev => {
          if (prev && incomplete.some(m => String(m.id) === String(prev))) return prev;
          const keepingOriginalProject = editTask && String(projectId) === String(editTask.project_id);
          if (keepingOriginalProject && editTask.milestone_id && incomplete.some(m => String(m.id) === String(editTask.milestone_id))) {
            return String(editTask.milestone_id);
          }
          return '';
        });
      })
      .catch(() => setMilestones([]))
      .finally(() => setMilestonesLoading(false));
  }, [open, appId, projectId, api]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selects a service and pulls its Major Activities + Deliverables in as
  // checklist items — replaces items auto-added from a previously selected
  // service, keeps ones the user typed themselves by hand before picking one.
  const selectAgreementService = useCallback((s, allServices) => {
    setAgreementServiceId(String(s.id));
    setServiceDropOpen(false);
    // Default Service Month/Year to the current month when picking a service
    // directly (no Compliance Board context to inherit it from) — changeable
    // right after, so backfilling a prior month stays one click away rather
    // than silently defaulting to "today" with no way to tell.
    setServiceMonth(prev => prev || String(new Date().getMonth() + 1));
    setServiceYear(prev => prev || String(new Date().getFullYear()));
    const auto = [...(s.deliverables || []), ...(s.major_activities || [])];
    setChecklistItems(prev => {
      const priorAuto = new Set(
        allServices
          .filter(x => String(x.id) !== String(s.id))
          .flatMap(x => [...(x.deliverables || []), ...(x.major_activities || [])])
      );
      const handTyped = prev.filter(t => !priorAuto.has(t));
      const merged = [...handTyped];
      auto.forEach(t => { if (!merged.includes(t)) merged.push(t); });
      return merged;
    });
  }, []);

  /* ── load services attached to the selected agreement ── */
  useEffect(() => {
    if (!open || !appId || !contractId) { setAgreementServices([]); setAgreementServiceId(''); setServiceSearch(''); return; }
    setAgreementServicesLoading(true);
    api.getContractAgreement(appId, contractId)
      .then(agreement => {
        const services = agreement?.services || [];
        setAgreementServices(services);
        // The Agreement field displays contractSearch, not contractId — when
        // pre-selecting via defaultContractId (opened from Compliance Board)
        // nothing had ever set contractSearch, so the field looked empty even
        // though an agreement was actually selected underneath.
        setContractSearch(prev => prev || agreement?.agreement_name || agreement?.agreement_number || (agreement?.id != null ? `Agreement #${agreement.id}` : ''));
        // Opened from Compliance Board with a specific service in mind —
        // pre-select it (and its checklist) once the agreement's services
        // have loaded, same as if the user had clicked it in the dropdown.
        // In edit mode, restore the task's own already-linked service instead
        // (defaultAgreementServiceIdRef isn't set there) — otherwise every
        // edit (e.g. just changing the assignee) would silently wipe
        // agreement_service_id, since it's reset to '' whenever contractId
        // changes and was never being restored here.
        const wanted = isEditMode ? editTask?.agreement_service_id : defaultAgreementServiceIdRef.current;
        if (wanted) {
          const match = services.find(s => String(s.id) === String(wanted));
          if (match) selectAgreementService(match, services);
        }
      })
      .catch(() => setAgreementServices([]))
      .finally(() => setAgreementServicesLoading(false));
  }, [open, appId, contractId, api, isEditMode, selectAgreementService]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── load contracts ── */
  useEffect(() => {
    if (!open || !appId || taskType !== 'contract') return;
    api.listContractAgreements(appId, {})
      .then(data => setContracts(Array.isArray(data) ? data : (data.agreements || data.items || [])))
      .catch(() => setContracts([]));
  }, [open, appId, taskType, api]);

  /* ── reset on open ── */
  useEffect(() => {
    if (!open) descCloseMention();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setError(null); setSubmitting(false);
    setJoinSent(false); setConfirmJoin(false);
    setRoleExpanded(false); setAreaExpanded(false); setRoutineExpanded(false);
    setAssigneeSearch('');

    if (editTask) {
      // pre-fill from existing task
      setTitle(editTask.title || '');
      setDescription(editTask.description || '');
      setPriority(editTask.priority === 'critical' ? 'urgent' : (editTask.priority || 'medium'));
      setDueOn(editTask.due_on || '');
      const estMin = editTask.estimated_minutes || 0;
      setEstMinutes(estMin);
      const preset = EST_PRESETS.find(p => p.value === estMin);
      setCustomEst(!preset && estMin > 0);
      setCustomEstInput(!preset && estMin > 0 ? String(estMin) : '');
      setAssigneeId(editTask.assigned_to_user_id ? String(editTask.assigned_to_user_id) : '');
      setAreaId(editTask.area_id ? String(editTask.area_id) : '');
      setRoutineId(editTask.routine_id ? String(editTask.routine_id) : '');
      // derive role from area
      if (editTask.area_id) {
        for (const [rid, areas] of areasByRole.entries()) {
          if (areas.some(a => a.id === editTask.area_id)) { setRoleId(String(rid)); break; }
        }
      } else {
        setRoleId('');
      }
      // task type
      if (editTask.project_id) {
        setTaskType('project');
        setProjectId(String(editTask.project_id));
        const p = projects.find(pr => String(pr.id) === String(editTask.project_id));
        setProjectSearch(p?.name || '');
      } else if (editTask.contract_id) {
        setTaskType('contract');
        setContractId(String(editTask.contract_id));
        setContractSearch('');
      } else {
        setTaskType('internal');
        setProjectId(''); setProjectSearch(''); setContractId(''); setContractSearch('');
      }
      setServiceMonth(editTask.service_month ? String(editTask.service_month) : '');
      setServiceYear(editTask.service_year ? String(editTask.service_year) : '');
      setMilestoneId('');
      setRoutineGroups({});
    } else {
      // create mode — reset to defaults
      const curMe      = meRef.current;
      const curMembers = membersRef.current;
      setTitle(''); setDescription('');
      setPriority('medium');
      setDueOn(defaultDueOn());
      setEstMinutes(45); setCustomEst(false); setCustomEstInput('');
      setRoleId(''); setAreaId(''); setRoutineId('');
      setTaskType(
        lockTaskType ? lockTaskType
        : defaultProjectIdRef.current  ? 'project'
        : defaultContractIdRef.current ? 'contract'
        : 'internal'
      );
      setProjectId(defaultProjectIdRef.current ? String(defaultProjectIdRef.current) : '');
      setProjectSearch('');
      setContractId(defaultContractIdRef.current ? String(defaultContractIdRef.current) : '');
      setContractSearch('');
      setAgreementServiceId(defaultAgreementServiceIdRef.current ? String(defaultAgreementServiceIdRef.current) : '');
      setServiceMonth(defaultServiceMonthRef.current ? String(defaultServiceMonthRef.current) : '');
      setServiceYear(defaultServiceYearRef.current ? String(defaultServiceYearRef.current) : '');
      setMilestoneId('');
      const curDefaultAssignee = defaultAssigneeUserIdRef.current;
      const defaultInMembers = curDefaultAssignee && curMembers?.find(m => String(m.user_id) === String(curDefaultAssignee));
      const meInMembers = curMembers?.find(m => m.user_id === curMe?.id);
      setAssigneeId(
        defaultInMembers ? String(curDefaultAssignee)
          : meInMembers ? String(curMe.id)
          : curMembers?.length ? String(curMembers[0].user_id) : ''
      );
      setRoutineGroups({});
    }
    setChecklistItems([]);
    setChecklistInput('');
    setEditChecklist([]);
    setEditChkInput('');
    if (open && editTask?.id) {
      api.listChecklist(appId, editTask.id)
        .then(r => setEditChecklist(r.items || []))
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── sync description text into contenteditable on open ── */
  useEffect(() => {
    if (!open) {
      if (descRef.current) descRef.current.innerHTML = '';
      descSavedRange.current = null;
      setDescCharCount(0);
      setDescFileErrors([]);
      return;
    }
    // Defer one tick so the modal DOM is mounted before we write to the ref
    const timer = setTimeout(() => {
    if (!descRef.current) return;
    const el = descRef.current;
    el.innerHTML = '';

    const savedAtts = isEditMode ? (editTask?.description_attachments || []) : [];
    const rawDesc = (isEditMode ? (editTask?.description || '') : (description || ''));

    if (!savedAtts.length) {
      // No attachments — just set text, stripping any stale placeholders
      const cleaned = rawDesc.replace(/\n?\[attachment: [^\]]+\]\n?/g, '').replace(/^\n|\n$/g, '');
      const html = cleaned.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
        .replace(/@\[(\d+):([^\]]+)\]/g, (_, id, name) =>
          `<span class="mention-chip" contenteditable="false" data-mention-id="${id}" data-mention-name="${name}">@${name}</span>`
        );
      el.innerHTML = html;
      setDescCharCount(cleaned.replace(/@\[(\d+):([^\]]*)\]/g, '@$2').length);
      return;
    }

    // Build a map of filename/id → attachment for lookup
    const attByName = {};
    const attById = {};
    for (const a of savedAtts) {
      if (a.file_name) attByName[a.file_name] = a;
      attById[String(a.id)] = a;
    }

    // Split raw description on placeholders and rebuild inline
    const parts = rawDesc.split(/\n?\[attachment: ([^\]]+)\]\n?/);
    let charCount = 0;
    parts.forEach((part, i) => {
      if (i % 2 === 0) {
        // Text segment — convert mention tokens to chips, preserve line breaks
        if (part) {
          const html = part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/\n/g, '<br>')
            .replace(/@\[(\d+):([^\]]+)\]/g, (_, id, name) =>
              `<span class="mention-chip" contenteditable="false" data-mention-id="${id}" data-mention-name="${name}">@${name}</span>`
            );
          const span = document.createElement('span');
          span.innerHTML = html;
          el.appendChild(span);
          charCount += part.replace(/@\[(\d+):([^\]]*)\]/g, '@$2').length;
        }
      } else {
        // Support __id_X__ format (new) and legacy filename/id format
        const idMatch = part.match(/^__id_(\d+)__$/);
        const att = idMatch ? attById[idMatch[1]] : (attByName[part] || attById[part]);
        if (att) {
          const node = makeAttachmentNode({ savedAtt: att });
          el.appendChild(node);
        }
      }
    });
    setDescCharCount(charCount);
    }, 0);
    return () => clearTimeout(timer);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── native file input listener ── */
  useEffect(() => {
    if (!open) return;
    const input = descFileInputRef.current;
    if (!input) return;
    const handler = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) addDescFiles(files);
      e.target.value = '';
    };
    input.addEventListener('change', handler);
    return () => input.removeEventListener('change', handler);
  }, [isEditMode, open]);

  const handleDescPaste = useCallback((e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const pastedFiles = items
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (pastedFiles.length) {
      e.preventDefault();
      addDescFiles(pastedFiles);
      return;
    }
    // Paste plain text only to avoid rich HTML with embedded images
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html || text) {
      e.preventDefault();
      const plain = text || '';
      const currentLen = countDescTextChars();
      const remaining = DESC_TEXT_LIMIT - currentLen;
      if (remaining > 0) {
        document.execCommand('insertText', false, plain.slice(0, remaining));
      }
    }
  }, []);

  const uploadDescAttachments = useCallback(async (taskId, files) => {
    if (!files.length) return;
    setDescUploading(true);
    try {
      const uploaded = await uploadDescFiles(files);
      for (const att of uploaded) {
        await api.addDescriptionAttachment(appId, taskId, att);
      }
    } catch (e) {
      alert(e.message || 'Attachment upload failed');
    } finally {
      setDescUploading(false);
    }
  }, [uploadDescFiles, api, appId]);

  /* ── fetch assignee workload when assignee changes ── */
  useEffect(() => {
    if (!open || !assigneeId || !appId) { setAssigneeWorkload(null); return; }
    api.assigneeWorkload(appId, assigneeId)
      .then(setAssigneeWorkload)
      .catch(() => setAssigneeWorkload(null));
  }, [open, assigneeId, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── routines for assignee ── */
  const fetchRoutines = useCallback(async (uid) => {
    if (!uid) { setRoutineGroups({}); return; }
    setRoutinesLoading(true);
    try {
      const data = await api.getAvailableRoutines(appId, uid);
      const grouped = data.grouped || {};
      setRoutineGroups(grouped);
      const all = Object.values(grouped).flat();
      const keepingOriginalAssignee = editTask && String(uid) === String(editTask.assigned_to_user_id);
      if (keepingOriginalAssignee && editTask.routine_id && all.some(r => r.id === editTask.routine_id)) {
        setRoutineId(String(editTask.routine_id));
      } else {
        const first = all[0];
        setRoutineId(first ? String(first.id) : '');
      }
    } catch { setRoutineGroups({}); }
    finally { setRoutinesLoading(false); }
  }, [api, appId, editTask]);

  useEffect(() => {
    if (open && assigneeId) fetchRoutines(assigneeId);
  }, [open, assigneeId, fetchRoutines]);

  /* ── role assignments for selected assignee ── */
  useEffect(() => {
    if (!open || !assigneeId) { setAssigneeRoles([]); setRoleId(''); return; }
    api.listAssignmentsForUser(appId, assigneeId)
      .then(data => {
        const items = data.items || data || [];
        setAssigneeRoles(items);
        const validRoles = roles.filter(r => new Set(items.map(i => i.role_id)).has(r.id));

        const keepingOriginalAssignee = editTask && String(assigneeId) === String(editTask.assigned_to_user_id);
        if (keepingOriginalAssignee && editTask.area_id) {
          for (const [rid, areas] of areasByRole.entries()) {
            if (areas.some(a => a.id === editTask.area_id)) { setRoleId(String(rid)); return; }
          }
        }
        const firstRole = validRoles[0] || roles[0];
        setRoleId(firstRole ? String(firstRole.id) : '');
      })
      .catch(() => { setAssigneeRoles([]); setRoleId(''); });
  }, [open, assigneeId, api, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── derived ── */
  const selectedMember    = members.find(m => String(m.user_id) === String(assigneeId));
  const areasForRole      = roleId ? (areasByRole.get(Number(roleId)) || []) : [];

  /* auto-select an area when role changes — keep the current selection if it's still valid for this role */
  useEffect(() => {
    if (areasForRole.some(a => String(a.id) === String(areaId))) return;
    if (areasForRole.length > 0) setAreaId(String(areasForRole[0].id));
    else setAreaId('');
  }, [roleId]); // eslint-disable-line react-hooks/exhaustive-deps
  const allRoutines       = Object.entries(routineGroups).flatMap(([, rs]) => rs);
  const selectedProject   = projects.find(p => String(p.id) === String(projectId));
  const isNonMember       = selectedProject && selectedProject.is_member === false;

  /* filter roles to only those assigned to the selected assignee */
  const assigneeRoleIdSet = new Set(assigneeRoles.map(r => r.role_id));
  const rolesForUser      = assigneeId && assigneeRoles.length > 0
    ? roles.filter(r => assigneeRoleIdSet.has(r.id))
    : roles;

  /* Live per-task cost estimate — only relevant for per-hour-billed projects, admins only.
     Rate = highest yearly CTC among the assignee's roles, divided by CTC_HOURLY_DIVISOR. */
  const isPerHourProject = taskType === 'project' && selectedProject?.billing_type === 'per_hour';
  const assigneeHourlyRate = (() => {
    if (!assigneeId || assigneeRoleIdSet.size === 0) return null;
    let max = null;
    for (const role of roles) {
      if (!assigneeRoleIdSet.has(role.id) || role.max_yearly_ctc == null) continue;
      const rate = Number(role.max_yearly_ctc) / CTC_HOURLY_DIVISOR;
      if (max === null || rate > max) max = rate;
    }
    return max;
  })();
  const estimatedTaskCost = (isAdmin && isPerHourProject && assigneeHourlyRate != null)
    ? (estMinutes / 60) * assigneeHourlyRate
    : null;

  const filteredMembers   = members.filter(m =>
    memberDisplayName(m.first_name, m.last_name, m.email)
      .toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  // Inactive (on-hold) and deleted projects can't be picked for a task.
  // `projects` itself keeps them so a task that already belongs to one still
  // shows its name.
  const filteredProjects  = projects.filter(p =>
    !p.is_inactive && !p.is_deleted &&
    (!projectSearch || p.name.toLowerCase().includes(projectSearch.toLowerCase()))
  );

  const filteredContracts = contracts.filter(c => {
    const label = c.agreement_name || c.agreement_number || '';
    return !contractSearch || label.toLowerCase().includes(contractSearch.toLowerCase());
  });

  /* ── submit ── */
  const taskPayload = () => ({
    title:               title.trim(),
    description:         (descRef.current ? extractDescData().text : description.trim()) || null,
    priority,
    due_on:              dueOn,
    area_id:             areaId ? Number(areaId) : null,
    assigned_to_user_id: assigneeId ? Number(assigneeId) : null,
    estimated_minutes:   estMinutes || 0,
    project_id:          taskType === 'project'  && projectId  ? Number(projectId)  : null,
    milestone_id:        taskType === 'project'  && projectId && milestoneId ? Number(milestoneId) : null,
    contract_id:         taskType === 'contract' && contractId ? Number(contractId) : null,
    agreement_service_id: taskType === 'contract' && contractId && agreementServiceId ? Number(agreementServiceId) : null,
    service_month:       taskType === 'contract' && contractId && agreementServiceId && serviceMonth ? Number(serviceMonth) : null,
    service_year:        taskType === 'contract' && contractId && agreementServiceId && serviceYear  ? Number(serviceYear)  : null,
    routine_id:          routineId ? Number(routineId) : null,
    ...(businessReviewId ? { business_review_id: businessReviewId } : {}),
    ...(businessReviewFor ? { business_review_for: Number(businessReviewFor) } : {}),
  });

  const doCreateTask = async () => {
    const { files: inlineFileEntries, text: rawDescText } = extractDescData();

    // Upload inline description files before saving so we can embed real IDs
    let finalDescText = rawDescText;
    if (inlineFileEntries.length) {
      setDescUploading(true);
      try {
        const rawUploaded = await uploadDescFiles(inlineFileEntries.map(e => e.file));
        // We don't have a task ID yet — create task first with placeholder text,
        // then register attachments and patch description in one follow-up update.
        // So save the uploaded Spaces results keyed by placeholder for now.
        const spacesAtts = rawUploaded.map((att, i) => ({ key: inlineFileEntries[i].key, att }));
        // Create the task with placeholder keys in description (they'll be fixed below)
        const payload = taskPayload();
        payload.description = finalDescText || null;
        const created = await api.createTask(appId, payload);
        if (created?.id) {
          // Register each uploaded file and replace placeholder with real __id_X__
          for (const { key, att } of spacesAtts) {
            const saved = await api.addDescriptionAttachment(appId, created.id, att);
            if (saved?.id) {
              finalDescText = finalDescText.replace(
                `[attachment: ${key}]`,
                `[attachment: __id_${saved.id}__]`
              );
            }
          }
          // Patch description with resolved IDs
          await api.updateTask(appId, created.id, { description: finalDescText || null });
          if (checklistItems.length > 0) {
            await Promise.all(checklistItems.map(text => api.addChecklistItem(appId, created.id, { text })));
          }
        }
      } finally {
        setDescUploading(false);
      }
    } else {
      const created = await api.createTask(appId, taskPayload());
      if (checklistItems.length > 0 && created?.id) {
        await Promise.all(checklistItems.map(text => api.addChecklistItem(appId, created.id, { text })));
      }
    }
    await onCreated?.();
    onClose?.();
  };

  const doUpdateTask = async () => {
    const { files: inlineFileEntries, keptAtts, text: rawDescText } = extractDescData();

    // Upload new files first so we have their IDs to put in description text
    let uploadedNewAtts = [];
    if (inlineFileEntries.length) {
      setDescUploading(true);
      try {
        const rawUploaded = await uploadDescFiles(inlineFileEntries.map(e => e.file));
        for (let i = 0; i < rawUploaded.length; i++) {
          const saved = await api.addDescriptionAttachment(appId, editTask.id, rawUploaded[i]);
          if (saved?.id) {
            uploadedNewAtts.push({ key: inlineFileEntries[i].key, att: saved });
          }
        }
      } finally {
        setDescUploading(false);
      }
    }

    // Replace __new_N__ keys with __id_X__ now that we have real IDs
    let finalDescText = rawDescText;
    for (const { key, att } of uploadedNewAtts) {
      finalDescText = finalDescText.replace(
        `[attachment: ${key}]`,
        `[attachment: __id_${att.id}__]`
      );
    }

    // Delete removed saved attachments
    const keptIds = new Set(keptAtts.map(a => a.id));
    const removedAtts = (editTask.description_attachments || []).filter(a => !keptIds.has(a.id));
    if (removedAtts.length) {
      await Promise.all(removedAtts.map(a => api.removeDescriptionAttachment(appId, editTask.id, a.id).catch(() => {})));
    }

    // Save task with the finalized description text
    const payload = taskPayload();
    payload.description = finalDescText || null;
    await api.updateTask(appId, editTask.id, payload);
    await onUpdated?.();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) { setError('Title is required'); return; }
    if (!dueOn)        { setError('Deadline is required'); return; }
    if (editTask && !assigneeId) { setError('Assignee is required'); return; }
    if (!isEditMode && dueOn < new Date().toISOString().slice(0, 10)) {
      setError('Deadline cannot be in the past.');
      return;
    }
    if (taskType === 'project' && projectId && !milestoneId) {
      setError('Select a milestone for this project task');
      return;
    }
    if (taskType === 'contract' && contractId && agreementServiceId) {
      const selectedSvc = agreementServices.find(s => String(s.id) === String(agreementServiceId));
      if (selectedSvc?.periodicity !== 'one_time' && (!serviceMonth || !serviceYear)) {
        setError('Select a Service Month for this recurring service task');
        return;
      }
    }
    if (isEditMode) {
      setSubmitting(true);
      try { await doUpdateTask(); }
      catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to save task');
        setSubmitting(false);
      }
      return;
    }

    if (joinSent) {
      setSubmitting(true);
      try {
        const fresh = await api.listProjectsSimple(appId);
        setProjects(fresh || []);
        const fp = (fresh || []).find(p => String(p.id) === String(projectId));
        if (!fp || fp.is_member === false) {
          setError('Still waiting for the owner to accept your join request.');
          setSubmitting(false); return;
        }
        setJoinSent(false);
        await doCreateTask();
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to create task');
        setSubmitting(false);
      }
      return;
    }

    if (isNonMember && !confirmJoin) { setConfirmJoin(true); return; }

    setSubmitting(true);
    try { await doCreateTask(); }
    catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create task');
      setSubmitting(false);
    }
  };

  const handleSendJoinRequest = async () => {
    setSubmitting(true); setError(null);
    try {
      await api.requestProjectJoin(appId, selectedProject.id);
      setJoinSent(true); setConfirmJoin(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send join request');
    } finally { setSubmitting(false); }
  };

  /* ── probable deadline calculation ── */
  const probableDeadline = (() => {
    if (!assigneeWorkload) return null;
    const minHrsDay = assigneeWorkload.min_hours_per_day || 0;
    if (minHrsDay <= 0) return null;
    const totalPendingMins = (assigneeWorkload.pending_minutes || 0) + (estMinutes || 0);
    const daysNeeded = Math.ceil(totalPendingMins / (minHrsDay * 60));
    if (daysNeeded <= 0) return null;
    const d = new Date();
    d.setDate(d.getDate() + daysNeeded);
    return d.toISOString().slice(0, 10);
  })();
  const deadlineTooEarly = probableDeadline && dueOn && dueOn < probableDeadline;

  /* ── shared inline styles ── */
  const chipRow = { display: 'flex', flexWrap: 'wrap', gap: 6 };

  const searchInputStyle = {
    width: '100%', paddingLeft: 30, paddingRight: 10, height: 30,
    borderRadius: 7, border: '1.5px solid var(--border)',
    background: 'var(--bg-muted)', fontSize: 12.5,
    color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
  };

  const searchDropStyle = {
    marginTop: 5, maxHeight: 160, overflowY: 'auto',
    border: '1px solid var(--border)', borderRadius: 8,
    background: 'var(--bg-card)', boxShadow: '0 4px 14px rgba(0,0,0,.1)',
  };

  const searchItemStyle = (active) => ({
    width: '100%', padding: '7px 12px', border: 'none',
    background: active ? 'rgba(59,130,246,.07)' : 'transparent',
    cursor: 'pointer', textAlign: 'left', fontSize: 12.5,
    color: 'var(--text-primary)', display: 'block',
  });

  const rolesForChips    = roleExpanded   ? rolesForUser  : rolesForUser.slice(0, CHIP_MAX);
  const areasForChips    = areaExpanded   ? areasForRole  : areasForRole.slice(0, CHIP_MAX);
  const routinesForChips = routineExpanded ? allRoutines   : allRoutines.slice(0, CHIP_MAX);

  /* ── render ── */
  return (
    <Modal open={open} title={isEditMode ? 'Edit Task' : 'New Task'} onClose={submitting ? undefined : onClose} size="2xl">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* subtitle */}
        <p style={{ margin: '0 0 6px', fontSize: 12.5, color: 'var(--text-muted)' }}>
          Create a new task and assign it to your team
        </p>

        {/* Row 1 — Task Name + Description (left) | Assigned To, Deadline,
            Priority, Estimation stacked (right). Wraps to one column when narrow. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'stretch' }}>
        <Card style={{ padding: '11px 15px', overflow: 'visible', flex: '1.6 1 420px', display: 'flex', flexDirection: 'column' }}>
          <div>
            <CardLabel icon={IC.taskName} text="Task Name" required />
            <textarea
              className="form-input"
              rows={2}
              value={title}
              onChange={e => setTitle(e.target.value.slice(0, 120))}
              placeholder="What needs to be done?"
              autoFocus
              style={{ resize: 'none', fontSize: 13, width: '100%' }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
              {title.length}/120
            </div>
          </div>
          <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', lineHeight: 1 }}>
                <span style={{ display: 'flex', flexShrink: 0, marginBottom: 2 }}>{IC.desc}</span>
                Description
              </div>
              <>
                <button
                  type="button"
                  onClick={() => { descSaveRange(); descFileInputRef.current?.click(); }}
                  title="Attach file"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)', background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  Attach
                </button>
                <input ref={descFileInputRef} type="file" multiple accept={ACCEPT_STRING} style={{ display: 'none' }} />
              </>
            </div>
            <div className="desc-editor-wrap" style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div
                ref={descRef}
                contentEditable
                suppressContentEditableWarning
                onBlur={descSaveRange}
                onMouseUp={descSaveRange}
                onKeyUp={(e) => {
                  descSaveRange(e);
                  setDescCharCount(countDescTextChars());
                  descMentionInput();
                }}
                onPaste={handleDescPaste}
                onInput={() => {
                  setDescCharCount(Math.min(countDescTextChars(), DESC_TEXT_LIMIT));
                  descMentionInput();
                }}
                onKeyDown={(e) => {
                  if (descMentionKeyDown(e)) return;
                  const len = countDescTextChars();
                  const isModifier = e.ctrlKey || e.metaKey || e.altKey;
                  const isNavigationOrEdit = [
                    'Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown',
                    'Home','End','Tab','Enter',
                  ].includes(e.key);
                  if (len >= DESC_TEXT_LIMIT && !isModifier && !isNavigationOrEdit) {
                    e.preventDefault();
                  }
                }}
                data-placeholder="Optional details... (type @ to mention)"
                style={{
                  flex: 1, minHeight: 100, maxHeight: 380, overflowY: 'auto',
                  padding: '8px 10px', outline: 'none', fontSize: 13,
                  lineHeight: 1.6, color: 'var(--text-primary)',
                  background: 'var(--bg-card)',
                }}
              />
              <DescMentionDropdown />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3 }}>
            <div style={{ fontSize: 12, color: 'var(--color-danger, #e53e3e)' }}>
              {descFileErrors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
            <div style={{ fontSize: 11, color: descCharCount > DESC_TEXT_LIMIT * 0.9 ? 'var(--color-danger, #e53e3e)' : 'var(--text-muted)' }}>
              {descCharCount} / {DESC_TEXT_LIMIT}
            </div>
          </div>
        </Card>

        {/* Right column — Assigned To | Deadline | Priority | Estimation */}
        <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Assigned To — inline search, no floating dropdown */}
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.user} text="Assigned To" required />
            {selectedMember ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 7, minWidth: 0,
                border: '1.5px solid var(--border)', borderRadius: 7,
                padding: '5px 9px', background: 'var(--bg-muted)',
                overflow: 'hidden',
              }}>
                <MemberAvatar member={selectedMember} size={22} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {memberDisplayName(selectedMember.first_name, selectedMember.last_name, selectedMember.email)}
                  {me && selectedMember.user_id === me.id ? ' (you)' : ''}
                </span>
                <button type="button" onClick={() => { setAssigneeId(''); setAssigneeSearch(''); }}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 0 }}>
                  {IC.xCircle}
                </button>
              </div>
            ) : (
              <>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'flex', pointerEvents: 'none' }}>
                    {IC.search}
                  </span>
                  <input
                    type="text" placeholder="Search member..."
                    value={assigneeSearch}
                    onChange={e => setAssigneeSearch(e.target.value)}
                    style={searchInputStyle}
                  />
                </div>
                {assigneeSearch && (
                  <div style={{ marginTop: 5, maxHeight: 130, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)' }}>
                    {filteredMembers.map(m => (
                      <button key={m.user_id} type="button"
                        onClick={() => { setAssigneeId(String(m.user_id)); setAssigneeSearch(''); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                          padding: '7px 10px', border: 'none', cursor: 'pointer', textAlign: 'left',
                          background: 'transparent', color: 'var(--text-primary)',
                        }}>
                        <MemberAvatar member={m} size={22} />
                        <span style={{ fontSize: 12.5 }}>
                          {memberDisplayName(m.first_name, m.last_name, m.email)}
                          {me && m.user_id === me.id ? ' (you)' : ''}
                        </span>
                      </button>
                    ))}
                    {!filteredMembers.length && (
                      <p style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No members found</p>
                    )}
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Deadline */}
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.calendar} text="Deadline" required />
            <div
              onClick={() => dateInputRef.current?.showPicker?.()}
              style={{
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${deadlineTooEarly ? '#f59e0b' : 'var(--border)'}`, borderRadius: 7,
                padding: '0 9px', background: 'var(--bg-muted)',
                height: 33, cursor: 'pointer',
              }}
            >
              <input
                ref={dateInputRef}
                type="date"
                value={dueOn}
                onChange={e => setDueOn(e.target.value)}
                style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 12.5, color: 'var(--text-primary)', outline: 'none', cursor: 'pointer' }}
              />
            </div>
            {probableDeadline && (
              <div style={{ marginTop: 5, fontSize: 11, lineHeight: 1.4 }}>
                {deadlineTooEarly ? (
                  <span style={{ color: '#f59e0b' }}>
                    ⚠ Earliest realistic deadline: <strong
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => setDueOn(probableDeadline)}
                    >{probableDeadline}</strong> (based on workload)
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>
                    Suggested deadline: <strong
                      style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--accent)' }}
                      onClick={() => setDueOn(probableDeadline)}
                    >{probableDeadline}</strong>
                  </span>
                )}
              </div>
            )}
          </Card>

          {/* Priority */}
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.flag} text="Priority" />
            <div style={chipRow}>
              {PRIORITY_OPTIONS.map(p => (
                <Chip key={p.value} selected={priority === p.value}
                  onClick={() => setPriority(p.value)} accentColor={p.color}>
                  <FlagIcon color={p.color} />
                  {p.label}
                </Chip>
              ))}
            </div>
          </Card>

          {/* Estimation */}
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.clock} text="Estimation" />
            <div style={chipRow}>
              {EST_PRESETS.map(e => (
                <Chip key={e.value}
                  selected={!customEst && estMinutes === e.value}
                  onClick={() => { setEstMinutes(e.value); setCustomEst(false); }}>
                  {e.label}
                </Chip>
              ))}
              {customEst ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <input
                    type="number" min={1}
                    value={customEstInput}
                    onChange={e => { setCustomEstInput(e.target.value); setEstMinutes(Number(e.target.value) || 0); }}
                    placeholder="min"
                    autoFocus
                    style={{
                      width: 64, padding: '4px 8px', borderRadius: 8,
                      border: '2px solid var(--accent)', fontSize: 12.5,
                      outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)',
                    }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>min</span>
                </div>
              ) : (
                <Chip selected={false}
                  onClick={() => { setCustomEst(true); setCustomEstInput(String(estMinutes)); }}>
                  Custom {IC.pencil}
                </Chip>
              )}
            </div>
            {estimatedTaskCost != null && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8 }}>
                Est. cost: <strong style={{ color: 'var(--text-primary)' }}>{estimatedTaskCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong>
              </div>
            )}
          </Card>
        </div>
        </div>

        {/* Checklist — edit mode: add input above, checkable list with delete below */}
        {isEditMode && (
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.pencil} text="Checklist" />
            <div style={{ display: 'flex', gap: 6, marginBottom: editChecklist.length ? 8 : 0 }}>
              <input
                type="text"
                className="form-input"
                value={editChkInput}
                onChange={e => setEditChkInput(e.target.value.slice(0, 500))}
                onKeyDown={async e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const t = editChkInput.trim();
                    if (!t || editChkAdding) return;
                    setEditChkAdding(true);
                    try {
                      const item = await api.addChecklistItem(appId, editTask.id, { text: t });
                      setEditChecklist(prev => [...prev, item]);
                      setEditChkInput('');
                    } catch (_) {}
                    finally { setEditChkAdding(false); }
                  }
                }}
                placeholder="Add item and press Enter..."
                maxLength={500}
                style={{ fontSize: 13, flex: 1 }}
                disabled={editChkAdding}
              />
              <button
                type="button"
                disabled={editChkAdding || !editChkInput.trim()}
                onClick={async () => {
                  const t = editChkInput.trim();
                  if (!t || editChkAdding) return;
                  setEditChkAdding(true);
                  try {
                    const item = await api.addChecklistItem(appId, editTask.id, { text: t });
                    setEditChecklist(prev => [...prev, item]);
                    setEditChkInput('');
                  } catch (_) {}
                  finally { setEditChkAdding(false); }
                }}
                style={{ padding: '0 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-muted)', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap', opacity: editChkAdding ? 0.6 : 1 }}
              >{editChkAdding ? 'Adding…' : '+ Add'}</button>
            </div>
            {editChecklist.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                {editChecklist.map(item => (
                  <li key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: item.is_done ? 'var(--text-muted)' : 'var(--text-primary)', background: 'var(--bg-muted)', borderRadius: 6, padding: '5px 9px' }}>
                    <input type="checkbox" checked={!!item.is_done} onChange={async () => {
                      const newDone = !item.is_done;
                      setEditChecklist(prev => prev.map(i => i.id === item.id ? { ...i, is_done: newDone } : i));
                      try { await api.updateChecklistItem(appId, editTask.id, item.id, { is_done: newDone }); }
                      catch (_) { setEditChecklist(prev => prev.map(i => i.id === item.id ? { ...i, is_done: item.is_done } : i)); }
                    }} style={{ cursor: 'pointer', accentColor: 'var(--primary, #1a5276)', flexShrink: 0 }} />
                    <span style={{ flex: 1, textDecoration: item.is_done ? 'line-through' : 'none' }}>{item.text}</span>
                    <button type="button" onClick={async () => {
                      setEditChecklist(prev => prev.filter(i => i.id !== item.id));
                      try { await api.removeChecklistItem(appId, editTask.id, item.id); }
                      catch (_) { setEditChecklist(prev => [...prev, item]); }
                    }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 0, flexShrink: 0 }}>{IC.xCircle}</button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {/* Row 4 — Task Type | Project / Contract | Milestone | Service Month */}
        {(() => {
          // Service Month only renders once a recurring (non one_time) service
          // is actually selected — see its own card below. Its presence adds
          // one more column to whichever contract-mode column count already
          // applied, so it lands in the same row as Agreement/Service instead
          // of wrapping onto its own line.
          const showsServiceMonth = taskType === 'contract' && contractId && agreementServiceId &&
            agreementServices.find(s => String(s.id) === String(agreementServiceId))?.periodicity !== 'one_time';
          const extraCol = showsServiceMonth ? ' 1fr' : '';
          const cols = lockTaskType
            ? (taskType === 'contract' && contractId ? '1fr 1fr' + extraCol : '1fr')
            : taskType === 'internal' ? '1fr'
            : taskType === 'project'  ? '1fr 1fr 1fr'
            : taskType === 'contract' && contractId ? '1fr 1fr 1fr' + extraCol
            : '1fr 1fr';
          return (
        <div style={{
          display: 'grid',
          gridTemplateColumns: cols,
          gap: 10,
        }}>

          {/* Task Type — hidden when the caller locks it (e.g. Compliance
              Board only ever creates Agreement-type tasks, so offering
              Internal/Project here would just be a confusing extra step). */}
          {!lockTaskType && (
            <Card>
              <CardLabel icon={IC.type} text="Task Type" />
              <div style={chipRow}>
                {TASK_TYPE_OPTIONS.map(t => (
                  <Chip key={t.value} selected={taskType === t.value}
                    onClick={() => {
                      setTaskType(t.value);
                      setProjectId(''); setProjectSearch('');
                      setContractId(''); setContractSearch('');
                      setAgreementServiceId(''); setServiceSearch('');
                      setJoinSent(false); setConfirmJoin(false); setError(null);
                    }}>
                    <TaskTypeIcon type={t.value} size={13} />
                    {t.label}
                  </Chip>
                ))}
              </div>
            </Card>
          )}

          {/* Project */}
          {taskType === 'project' && (
            <Card>
              <CardLabel icon={<TaskTypeIcon type="project" size={13} />} text="Project" />
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'flex', pointerEvents: 'none' }}>
                  {IC.search}
                </span>
                <input
                  type="text"
                  placeholder="Search project..."
                  value={projectSearch}
                  onChange={e => { setProjectSearch(e.target.value); setProjectId(''); setJoinSent(false); setConfirmJoin(false); setError(null); }}
                  style={{ ...searchInputStyle, paddingRight: isNonMember ? 100 : 10 }}
                />
                {isNonMember && (
                  <span style={{
                    position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)',
                    fontSize: 11, color: '#f59e0b', fontWeight: 600, pointerEvents: 'none',
                  }}>
                    not a member
                  </span>
                )}
              </div>
              {projectSearch && !projectId && (
                <div style={searchDropStyle}>
                  {filteredProjects.length ? filteredProjects.map(p => (
                    <button key={p.id} type="button" style={searchItemStyle(false)}
                      onClick={() => { setProjectId(String(p.id)); setProjectSearch(p.name); }}>
                      {p.name}
                    </button>
                  )) : (
                    <p style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No projects found</p>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Milestone — required once a project is selected; only shows
              milestones that aren't yet marked complete. */}
          {taskType === 'project' && (
            <Card style={{ overflow: 'visible' }}>
              <CardLabel icon={IC.flag} text="Milestone" required={!!projectId} />
              {!projectId ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Select a project first</span>
              ) : milestonesLoading ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</span>
              ) : milestones.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No incomplete milestones on this project
                </span>
              ) : (
                <div style={{ position: 'relative' }}>
                  <div
                    className="form-input"
                    tabIndex={0}
                    onClick={() => setMilestoneDropOpen(o => !o)}
                    onBlur={() => setTimeout(() => setMilestoneDropOpen(false), 150)}
                    style={{ fontSize: 13, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none', minHeight: 38 }}
                  >
                    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', flex: 1 }}>
                      {milestoneId ? (milestones.find(m => String(m.id) === String(milestoneId))?.title || 'Select milestone…') : 'Select milestone…'}
                    </span>
                    <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontSize: 10, flexShrink: 0 }}>▾</span>
                  </div>
                  {milestoneDropOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      border: '1px solid var(--border)', borderRadius: 8,
                      maxHeight: 200, overflowY: 'auto',
                      background: 'var(--bg-card)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                    }}>
                      <div
                        onMouseDown={() => { setMilestoneId(''); setMilestoneDropOpen(false); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}
                      >Select milestone…</div>
                      {milestones.map(m => (
                        <div
                          key={m.id}
                          onMouseDown={() => { setMilestoneId(String(m.id)); setMilestoneDropOpen(false); }}
                          style={{
                            padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                            background: String(milestoneId) === String(m.id) ? 'rgba(6,182,212,0.1)' : 'transparent',
                            borderBottom: '1px solid var(--border)',
                            whiteSpace: 'normal', wordBreak: 'break-word',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(6,182,212,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = String(milestoneId) === String(m.id) ? 'rgba(6,182,212,0.1)' : 'transparent'}
                        >{m.title}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Contract */}
          {taskType === 'contract' && (
            <Card style={{ overflow: 'visible' }}>
              <CardLabel icon={<TaskTypeIcon type="contract" size={13} />} text="Agreement" />
              {contractId ? (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 7,
                  border: '1.5px solid var(--border)', borderRadius: 7,
                  padding: '5px 9px', background: 'var(--bg-muted)',
                }}>
                  <TaskTypeIcon type="contract" size={14} />
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {contractSearch}
                  </span>
                  <button type="button" onClick={() => {
                    setContractId(''); setContractSearch('');
                    // Clearing the agreement removes its service too, so also
                    // drop any checklist items that came from that service —
                    // otherwise they'd linger with nothing left selecting them.
                    const wasSelectedSvc = agreementServices.find(s => String(s.id) === String(agreementServiceId));
                    setAgreementServiceId('');
                    if (wasSelectedSvc) {
                      const auto = new Set([...(wasSelectedSvc.deliverables || []), ...(wasSelectedSvc.major_activities || [])]);
                      setChecklistItems(prev => prev.filter(t => !auto.has(t)));
                    }
                  }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 0 }}>
                    {IC.xCircle}
                  </button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'flex', pointerEvents: 'none' }}>
                      {IC.search}
                    </span>
                    <input
                      type="text"
                      placeholder="Search agreement..."
                      value={contractSearch}
                      onChange={e => { setContractSearch(e.target.value); setContractId(''); }}
                      onFocus={() => setContractDropOpen(true)}
                      onBlur={() => setTimeout(() => setContractDropOpen(false), 150)}
                      style={searchInputStyle}
                    />
                  </div>
                  {(contractDropOpen || contractSearch) && (
                    <div style={{ ...searchDropStyle, position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, maxHeight: 200 }}>
                      {filteredContracts.length ? filteredContracts.map(c => (
                        <button key={c.id} type="button"
                          style={searchItemStyle(false)}
                          onMouseDown={() => {
                            setContractId(String(c.id));
                            setContractSearch(c.agreement_name || c.agreement_number || `Agreement #${c.id}`);
                            setContractDropOpen(false);
                          }}>
                          {c.agreement_name || c.agreement_number || `Agreement #${c.id}`}
                        </button>
                      )) : (
                        <p style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No agreements found</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Service — once an agreement is selected, choosing a service pulls
              its Major Activities / Deliverables in as checklist items. Same
              search-input + floating-list pattern as the Agreement field
              above, for a consistent look and interaction. */}
          {taskType === 'contract' && contractId && (
            <Card style={{ overflow: 'visible' }}>
              <CardLabel icon={IC.pencil} text="Service" />
              {agreementServicesLoading ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</span>
              ) : agreementServices.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No services on this agreement
                </span>
              ) : agreementServiceId ? (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 7,
                  border: '1.5px solid var(--border)', borderRadius: 7,
                  padding: '5px 9px', background: 'var(--bg-muted)',
                }}>
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {agreementServices.find(s => String(s.id) === String(agreementServiceId))?.service_name || ''}
                  </span>
                  <button type="button" onClick={() => {
                    const wasSelectedSvc = agreementServices.find(s => String(s.id) === String(agreementServiceId));
                    setAgreementServiceId('');
                    setServiceSearch('');
                    if (wasSelectedSvc) {
                      const auto = new Set([...(wasSelectedSvc.deliverables || []), ...(wasSelectedSvc.major_activities || [])]);
                      setChecklistItems(prev => prev.filter(t => !auto.has(t)));
                    }
                  }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 0 }}>
                    {IC.xCircle}
                  </button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'flex', pointerEvents: 'none' }}>
                      {IC.search}
                    </span>
                    <input
                      type="text"
                      placeholder="Search service..."
                      value={serviceSearch}
                      onChange={e => setServiceSearch(e.target.value)}
                      onFocus={() => setServiceDropOpen(true)}
                      onBlur={() => setTimeout(() => setServiceDropOpen(false), 150)}
                      style={searchInputStyle}
                    />
                  </div>
                  {(serviceDropOpen || serviceSearch) && (() => {
                    const q = serviceSearch.trim().toLowerCase();
                    const filtered = q ? agreementServices.filter(s => (s.service_name || '').toLowerCase().includes(q)) : agreementServices;
                    return (
                      <div style={{ ...searchDropStyle, position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, maxHeight: 200 }}>
                        {filtered.length ? filtered.map(s => (
                          <button key={s.id} type="button"
                            style={{ ...searchItemStyle(false), whiteSpace: 'normal', wordBreak: 'break-word' }}
                            onMouseDown={() => { selectAgreementService(s, agreementServices); setServiceSearch(''); }}>
                            {s.service_name}
                          </button>
                        )) : (
                          <p style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No services found</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </Card>
          )}

          {/* Service Month — required for a recurring service so the
              Compliance Board can tell which period this task actually
              covers. Without it, one approved task for a monthly service
              would silently count as "done" for every month it's viewed,
              since nothing distinguished August's completion from
              October's. Not shown for one_time services, which have only
              one occurrence ever and nothing to disambiguate. Defaults to
              the current month (or the Compliance Board's viewed month,
              via defaultServiceMonth/Year) but is always changeable — e.g.
              to backfill a month that was missed. */}
          {taskType === 'contract' && contractId && agreementServiceId &&
            agreementServices.find(s => String(s.id) === String(agreementServiceId))?.periodicity !== 'one_time' && (
            <Card>
              <CardLabel icon={IC.calendar} text="Service Month" />
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={serviceMonth}
                  onChange={e => setServiceMonth(e.target.value)}
                  style={{
                    flex: 2, fontSize: 13, padding: '6px 9px',
                    border: '1.5px solid var(--border)', borderRadius: 7,
                    background: 'var(--bg-muted)', color: 'var(--text-primary)',
                  }}
                >
                  <option value="">— month —</option>
                  {SERVICE_MONTHS.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={serviceYear}
                  onChange={e => setServiceYear(e.target.value)}
                  style={{
                    flex: 1, fontSize: 13, padding: '6px 9px',
                    border: '1.5px solid var(--border)', borderRadius: 7,
                    background: 'var(--bg-muted)', color: 'var(--text-primary)',
                  }}
                >
                  <option value="">— year —</option>
                  {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </Card>
          )}

        </div>
          );
        })()}

        {/* Checklist — only in create mode. Placed after the Agreement/Service
            picker above (not right after Description) so items auto-filled
            from a selected service's Deliverables/Activities show up right
            where the user is looking, instead of scrolled out of view above
            the Agreement/Service fields that produced them. */}
        {!isEditMode && (
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.pencil} text="Checklist" />
            <div style={{ display: 'flex', gap: 6, marginBottom: checklistItems.length ? 8 : 0 }}>
              <input
                type="text"
                className="form-input"
                value={checklistInput}
                onChange={e => setChecklistInput(e.target.value.slice(0, 500))}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const t = checklistInput.trim();
                    if (t) { setChecklistItems(prev => [...prev, t]); setChecklistInput(''); }
                  }
                }}
                placeholder="Add item and press Enter..."
                maxLength={500}
                style={{ fontSize: 13, flex: 1 }}
              />
              <button
                type="button"
                onClick={() => {
                  const t = checklistInput.trim();
                  if (t) { setChecklistItems(prev => [...prev, t]); setChecklistInput(''); }
                }}
                style={{ padding: '0 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-muted)', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}
              >+ Add</button>
            </div>
            {checklistItems.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {checklistItems.map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-primary)', background: 'var(--bg-muted)', borderRadius: 6, padding: '5px 9px' }}>
                    <span style={{ flex: 1 }}>{item}</span>
                    <button
                      type="button"
                      onClick={() => setChecklistItems(prev => prev.filter((_, j) => j !== i))}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 0 }}
                    >{IC.xCircle}</button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {/* Row 5 — Role | Area | Routine Activity */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>

          {/* Role */}
          <Card style={{ opacity: !assigneeId ? 0.5 : 1 }}>
            <CardLabel icon={IC.role} text="Role" />
            <div style={chipRow}>
              {!assigneeId ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Select an assignee first</span>
              ) : (
                <>
                  <Chip selected={!roleId} onClick={() => { setRoleId(''); setAreaId(''); }}>None</Chip>
                  {rolesForChips.map(r => (
                    <Chip key={r.id} selected={String(r.id) === String(roleId)}
                      onClick={() => { setRoleId(String(r.id)); setAreaId(''); }}>
                      {r.title}
                    </Chip>
                  ))}
                  {rolesForUser.length > CHIP_MAX && (
                    <Chip selected={false} onClick={() => setRoleExpanded(x => !x)}>
                      {roleExpanded ? '− Less' : `+ ${rolesForUser.length - CHIP_MAX} More`}
                    </Chip>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* Area */}
          <Card style={{ opacity: !roleId ? 0.5 : 1 }}>
            <CardLabel icon={IC.area} text="Area" />
            <div style={chipRow}>
              {!roleId ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Select a role first</span>
              ) : areasForRole.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No areas</span>
              ) : (
                <>
                  <Chip selected={!areaId} onClick={() => setAreaId('')}>None</Chip>
                  {areasForChips.map(a => (
                    <Chip key={a.id} selected={String(a.id) === String(areaId)}
                      onClick={() => setAreaId(String(a.id))}>
                      {a.name}
                    </Chip>
                  ))}
                  {areasForRole.length > CHIP_MAX && (
                    <Chip selected={false} onClick={() => setAreaExpanded(x => !x)}>
                      {areaExpanded ? '− Less' : `+ ${areasForRole.length - CHIP_MAX} More`}
                    </Chip>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* Routine Activity */}
          <Card>
            <CardLabel icon={IC.routine} text="Routine Activity" />
            <div style={chipRow}>
              <Chip selected={!routineId} onClick={() => setRoutineId('')}>None</Chip>
              {routinesLoading ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</span>
              ) : (
                <>
                  {routinesForChips.map(r => (
                    <Chip key={r.id} selected={String(r.id) === String(routineId)}
                      onClick={() => setRoutineId(String(r.id))}>
                      {r.description || r.periodicity || `#${r.id}`}
                    </Chip>
                  ))}
                  {allRoutines.length > CHIP_MAX && (
                    <Chip selected={false} onClick={() => setRoutineExpanded(x => !x)}>
                      {routineExpanded ? '− Less' : `+ ${allRoutines.length - CHIP_MAX} More`}
                    </Chip>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>

        {/* Join prompts */}
        {confirmJoin && !joinSent && (
          <div style={{ background: '#fff8e1', border: '1px solid #f0a500', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#7a5500', textAlign: 'center' }}>
            <strong>You are not a member of "{selectedProject?.name}".</strong>
            <div style={{ marginTop: 4 }}>Send a join request to the project owner?</div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button type="button" className="btn-primary" style={{ fontSize: 12, padding: '4px 14px' }}
                onClick={handleSendJoinRequest} disabled={submitting}>
                {submitting ? 'Sending…' : 'OK, Send Request'}
              </button>
              <button type="button" className="btn-secondary" style={{ fontSize: 12, padding: '4px 14px' }}
                onClick={() => { setConfirmJoin(false); setProjectId(''); setProjectSearch(''); }} disabled={submitting}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {joinSent && (
          <div style={{ background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#1b5e20', textAlign: 'center' }}>
            Join request sent. Once accepted, click "Create Task" to proceed.
          </div>
        )}

        {error && <div className="form-error">{error}</div>}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {!confirmJoin && (
            <button type="submit" className="btn-primary" disabled={submitting}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
                <path d="M7 4.5v5M4.5 7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {submitting ? (isEditMode ? 'Saving…' : 'Creating…') : (isEditMode ? 'Save Changes' : 'Create Task')}
            </button>
          )}
        </div>

      </form>
    </Modal>
  );
}